import type { UniqueIdentifier } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { Icon } from '@chakra-ui/react';
import { MdDragHandle } from 'react-icons/md';
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Flex,
  Text,
  Heading,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { ColumnId } from "./KanbanBoard";
import { TaskCard } from "models/tasks_type";
import { useRef } from "react";
import { useLaunchedProject } from "../hooks/useLaunchedProject";

type TaskCardWithUseProps = Pick<useLaunchedProject.type, 'setShowTaskForm'>;

interface TaskCardProps extends TaskCardWithUseProps {
  task: TaskCard;
  isOverlay?: boolean;
}

export type TaskType = "Task";

export interface TaskDragData {
  type: TaskType;
  task: TaskCard;
}

export function TaskCardComponent({ task, isOverlay, setShowTaskForm }: TaskCardProps) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: {
      type: "Task",
      task,
    } satisfies TaskDragData,
    attributes: {
      roleDescription: "Task",
    },
  });

  const isLate = new Date(task.dataEstimativa) < new Date();

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  const bg = useColorModeValue("gray.50", "gray.700");
  const draggingStyle = isOverlay
    ? {
      boxShadow: "0 0 0 2px var(--chakra-colors-primary)",
      opacity: 0.7,
    }
    : isDragging
      ? {
        boxShadow: "0 0 0 2px var(--chakra-colors-primary)",
        opacity: 0.3,
      }
      : {};

  return (
    <Box
      ref={setNodeRef}
      style={{ ...style, ...draggingStyle }}
      bg={bg}
      borderRadius="md"
      p={3}
      boxShadow="sm"
      border="none"
    >
      <Flex
        px={3}
        py={2}
        align="center"
        justify="space-between"
        position="relative"
      >
        <Text fontSize={'sm'} fontWeight={'bold'} color="gray.700">
          {task.code}
        </Text>

        {isLate && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="20px"
            px={2}
            fontSize="10px"
            fontWeight="semibold"
            bg="orange.600"
            color="white"
            borderRadius="full"
            ml={2}
          >
            ATRASADO
          </Box>
        )}

        <Button
          variant="ghost"
          {...attributes}
          {...listeners}
          p={1}
          color="gray.500"
          ml="auto"
          h="auto"
          cursor="grab"
        >
          <span style={{ visibility: "hidden" }}>Move task</span>
          <Icon as={MdDragHandle} width='20px' height='20px' color='inherit' />
        </Button>
      </Flex>

      <Box px={3} pb={3} textAlign="left" display={'flex'} flexDirection={'column'}>
        <Heading size="sm" color="gray.700">
          {task.titulo}
        </Heading>

        <Text my={4} color="gray.500">
          {task.descricao}
        </Text>

        <Text fontSize={'sm'} fontWeight={'bold'} color="gray.600">Criado em {new Date(task.dataCriacao).toLocaleDateString("pt-BR")}</Text>

        <Text fontSize={'sm'} fontWeight={'bold'} color="gray.600">Vencimento em {new Date(task.dataEstimativa).toLocaleDateString("pt-BR")}</Text>

        <Button
          colorScheme='brand'
          size={"sm"}
          borderRadius={'lg'}
          variant='outline'
          mt={4}
          alignSelf={'flex-end'}
          onClick={() => setShowTaskForm(true, task)}
          // isLoading={state.loading}
        >
          Editar
        </Button>
      </Box>
    </Box>
  );
}
